import compact from "lodash/compact";
import includes from "lodash/includes";
import queryString from "query-string";
import stringScore from "string-score";

// These can override default options
const overrideQueryStringFlags = ["embedded"];

const recognizedQueryStringFlags = [
  "debug-analytics",
  "debug-analytics-with-stringified-warnings",
  "debug-video-playback",
  "design-mode",
  "disable-inactivity",
  "force-canvas-video",
  "force-initial-ad",
  "force-low-resolution-video",
  "force-persistent-video",
  "force-show-stall-button",
  "settings-menu",
  "show-seek-buttons",
  "verse-embedded",
];

const recognizedQueryStringsWithValues = ["story-sharing-url"];

const recognizedQueryStrings = [
  "autoplay",
  "captions",
  "chapterbuttons",
  "chapterprogress",
  "controls",
  "coverbutton",
  "covertitle",
  "cuepoints",
  "fitmode",
  "hamburger",
  "language",
  "playernav",
  "questionlist",
  "sharing",
  "slidearrows",
  "slidegallery",
  ...overrideQueryStringFlags,
  ...recognizedQueryStringFlags,
  ...recognizedQueryStringsWithValues,
];

function findClosestString(options: readonly string[], target: string) {
  return options.reduce((currentBest, option) => {
    const fuzz = 0.5;

    const bestScore = stringScore(option, target, fuzz);
    const newScore = stringScore(currentBest, target, fuzz);

    if (newScore < bestScore) {
      return option;
    }

    return currentBest;
  });
}

/**
 *
 * @param {Object} defaultValues Default values spread into the output
 * @param {Object} options
 * @param {string} options.windowLocationSearch Search params from URL
 * @param {boolean} options.offline If we're in offline mode or not
 */
export default function parsePlayerOptions(
  defaultValues: Record<string, unknown>,
  { windowLocationSearch = "", offline = false } = {},
) {
  const parsedQueryString = Object.freeze(
    queryString.parse(windowLocationSearch.replace(/embed\b/, "embedded")),
  );

  // Check for embedded flags in the URL
  let storiesEmbed = false;
  if (window && window.location) {
    let [routePrefix] = compact(window.location.pathname.split("/"));
    routePrefix = routePrefix || "";
    storiesEmbed = !!routePrefix.match("stories-embed");
  }

  // Provide warnings for unrecognized keys
  Object.keys(parsedQueryString)
    .filter((q) => !includes(recognizedQueryStrings, q))
    .forEach((unrecognizedQuery) => {
      //eslint-disable-next-line no-console
      console.warn(
        `Unrecognized query string "${unrecognizedQuery}", did you mean "${findClosestString(
          recognizedQueryStrings,
          unrecognizedQuery,
        )}"?`,
      );
    });

  function parseFlag(sourceObject: object, propertyName: string) {
    return (
      Object.prototype.hasOwnProperty.call(sourceObject, propertyName) &&
      parsedQueryString[propertyName] !== "0"
    );
  }

  const result = {
    ...defaultValues,
    disableCompanyNavbar: true,
    isStaticSite: offline,
    embedded:
      offline ||
      // If `embedded` is included, that means it was explicitly set and we must
      // obey that value. This is the only way to indicate that an iframe is
      // _not_ embedded, which we need for tests.
      Object.prototype.hasOwnProperty.call(parsedQueryString, "embedded")
        ? parseFlag(parsedQueryString, "embedded")
        : defaultValues.embedded ||
          defaultValues.embed ||
          parseFlag(parsedQueryString, "embedded") ||
          storiesEmbed ||
          false,
    loadingImageFocalPoint: window.loadingImageFocalPoint,
    autoplay:
      typeof parsedQueryString.autoplay === "string"
        ? parseInt(parsedQueryString.autoplay, 10)
        : 0,
    showClosedCaptions: parsedQueryString.captions === "1",
    chapterNavigationButtons:
      parsedQueryString.chapterbuttons === "0" ||
      parsedQueryString.controls === "0"
        ? "hide"
        : "show",
    showChapterNumber: parsedQueryString.controls === "2",
    hideChapterProgress: parsedQueryString.chapterprogress === "0",
    hidePlayerControls: parsedQueryString.controls === "0",
    hideCoverButton: parsedQueryString.coverbutton === "0",
    hideCoverTitle: parsedQueryString.covertitle === "0",
    hideCuepoints: parsedQueryString.cuepoints === "0",
    hideHamburger: parsedQueryString.hamburger === "0",
    defaultCaptionLanguage: parsedQueryString.language || undefined,
    hidePlayerNav: parsedQueryString.playernav === "0",
    hideQuestionList: parsedQueryString.questionlist === "0",
    disableSharing: parsedQueryString.sharing === "0",
    hideSlideArrows: parsedQueryString.slidearrows === "0",
    hideDefaultCaptions: parsedQueryString.slidegallery === "0",
    fitMode: (parsedQueryString.fitmode === "1"
      ? "fill"
      : parsedQueryString.fitmode === "2"
      ? "fit"
      : undefined) as "fill" | "fit" | undefined,
    financedUnlockedNavigationArrows: parsedQueryString.controls === "2",
    showSliderAboveControls:
      typeof parsedQueryString.controls === "undefined" ||
      parsedQueryString.controls === "2",
    showCoutdownTimerInControls:
      parsedQueryString.controls !== "0" &&
      parsedQueryString.chapterprogress !== "0",
    horizontalHotspotProgress:
      typeof parsedQueryString.controls === "undefined" ||
      parsedQueryString.controls === "2",
    showChapterTimeInChapterList: parsedQueryString.controls !== "0",

    // flags
    debugAnalytics: parseFlag(parsedQueryString, "debug-analytics"),
    debugAnalyticsWithStringifiedWarnings: parseFlag(
      parsedQueryString,
      "debug-analytics-with-stringified-warnings",
    ),
    debugVideoPlayback: parseFlag(parsedQueryString, "debug-video-playback"),
    designMode: parseFlag(parsedQueryString, "design-mode"),
    disableInactivity: parseFlag(parsedQueryString, "disable-inactivity"),
    forceInitialAd: parseFlag(parsedQueryString, "force-initial-ad"),
    forceLowResolutionVideo: parseFlag(
      parsedQueryString,
      "force-low-resolution-video",
    ),
    forcePersistentVideo: parseFlag(
      parsedQueryString,
      "force-persistent-video",
    ),
    forceShowStallButton: parseFlag(
      parsedQueryString,
      "force-show-stall-button",
    ),
    showSeekButtons: parsedQueryString.controls === "2",
    verseEmbedded: parseFlag(parsedQueryString, "verse-embedded"),

    // with raw values
    storySharingUrl: parsedQueryString["story-sharing-url"] || null,
    showSettingsMenu: parsedQueryString.controls === "2" ? "auto" : "hide",
  };

  return result;
}
